import type {
  DragEvent as ReactDragEvent, JSX, MouseEvent as ReactMouseEvent, ReactNode,
} from 'react';
import { Box, ButtonBase, styled } from '@mui/material';
import { GripVerticalIcon } from 'lucide-react';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import {
  HOVER_BACKGROUND, LABEL_WIDTH, ROW_HEIGHT, TIMELINE_COLOURS, TRACK_GRID_BACKGROUND,
} from '@/constants/PatientDashboard/patientTimeline';
import type { ReorderableSectionKey } from '@/types/PatientDashboard/PatientTimeline.types';

const TrackRowRoot = styled(Box)({
  display: 'flex',
  width: '100%',
  transition: 'background 120ms ease, opacity 120ms ease',
  '&:hover': {
    background: HOVER_BACKGROUND,
  },
  '&[data-dragging="true"]': {
    opacity: 0.52,
  },
  '&[data-drop-target="true"]': {
    background: TIMELINE_COLOURS.dropHighlight,
    boxShadow: `inset 0 0 0 1px ${corePalette.blue100}`,
  },
});

const TrackLabel = styled(Box)({
  width: `${LABEL_WIDTH}px`,
  height: `${ROW_HEIGHT}px`,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '0 12px',
});

const RowDragHandle = styled(ButtonBase)({
  width: '18px',
  height: '24px',
  minWidth: '18px',
  marginLeft: '-6px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  color: TIMELINE_COLOURS.dragHandle,
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
  '&:hover': {
    background: TIMELINE_COLOURS.dropHighlight,
    color: corePalette.grey100,
  },
});

const TrackLabelText = styled(CustomTypography)({
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 500,
  color: corePalette.offBlack100,
});

const TrackContent = styled(Box)({
  position: 'relative',
  flex: 1,
  height: `${ROW_HEIGHT}px`,
  minWidth: 0,
  overflow: 'hidden',
  ...TRACK_GRID_BACKGROUND,
});

const EmptyTrackContent = styled(Box)({
  height: `${ROW_HEIGHT}px`,
  flex: 1,
  ...TRACK_GRID_BACKGROUND,
});

type TrackRowDragHandler = (
  event: ReactDragEvent<HTMLElement>,
  section: ReorderableSectionKey,
  id: string,
) => void;

interface IProps {
  label: string;
  children?: ReactNode;
  section?: ReorderableSectionKey;
  id?: string;
  isDragging?: boolean;
  isDropTarget?: boolean;
  onDragStart?: TrackRowDragHandler;
  onDragOver?: TrackRowDragHandler;
  onDrop?: TrackRowDragHandler;
  onDragEnd?: () => void;
}

export default function TrackRow({
  label,
  children,
  section,
  id,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: IProps): JSX.Element {
  // Narrow once: the row is draggable only when section, id and every drag
  // callback are present, so the handlers below can pass the now-defined
  // section and id straight through. When any is missing, dragging is off.
  const dragHandlers = section && id && onDragStart && onDragOver && onDrop
    ? {
      handleDragStart: (event: ReactDragEvent<HTMLElement>) => onDragStart(event, section, id),
      handleDragOver: (event: ReactDragEvent<HTMLElement>) => onDragOver(event, section, id),
      handleDrop: (event: ReactDragEvent<HTMLElement>) => onDrop(event, section, id),
    }
    : null;

  const handleDragHandleClick = (event: ReactMouseEvent<HTMLElement>): void => {
    event.stopPropagation();
  };

  const handleDragHandleMouseDown = (event: ReactMouseEvent<HTMLElement>): void => {
    event.stopPropagation();
  };

  return (
    <TrackRowRoot
      data-dragging={isDragging ? 'true' : undefined}
      data-drop-target={isDropTarget ? 'true' : undefined}
      onDragOver={dragHandlers?.handleDragOver}
      onDrop={dragHandlers?.handleDrop}
    >
      <TrackLabel>
        {dragHandlers && (
          <RowDragHandle
            aria-label={`Drag ${label}`}
            disableRipple
            draggable
            onClick={handleDragHandleClick}
            onDragEnd={onDragEnd}
            onDragStart={dragHandlers.handleDragStart}
            onMouseDown={handleDragHandleMouseDown}
          >
            <GripVerticalIcon size={14} strokeWidth={2} />
          </RowDragHandle>
        )}
        <TrackLabelText truncate variant="bodySmall">
          {label}
        </TrackLabelText>
      </TrackLabel>
      {children ? (
        <TrackContent>{children}</TrackContent>
      ) : (
        <EmptyTrackContent />
      )}
    </TrackRowRoot>
  );
}
